import { KavachSwitch, KavachAlert } from './kavachTypes';

// KAVACH Brand Colors
export const KAVACH_COLORS = {
  primary: '#0056b3',
  secondary: '#003d82',
  accent: '#ff6b35',
  statusUp: '#28a745',
  statusDown: '#dc3545',
  statusWarning: '#ffc107',
  statusPartial: '#fd7e14',
  bgColor: '#f8fafc',
  cardBg: '#ffffff',
  textColor: '#1e293b',
  textSecondary: '#64748b',
  borderColor: '#e2e8f0',
  hoverColor: '#f1f5f9',
};

// Default station list (will be dynamically populated from API)
export const DEFAULT_STATIONS = ['ALL'];

/**
 * Get switch health status based on port states
 * @param sw - KavachSwitch object
 * @returns 'healthy' | 'partial' | 'critical'
 */
export const getSwitchHealth = (sw: KavachSwitch): 'healthy' | 'partial' | 'critical' => {
  const ports = Object.values(sw.ports);
  const downCount = ports.filter(p => p.status === 'DOWN').length;

  if (downCount === 0) return 'healthy';
  if (downCount >= 3) return 'critical';
  return 'partial';
};

/**
 * Get count of ports up/down for a switch
 * @param sw - KavachSwitch object
 * @returns { up: number, down: number }
 */
export const getPortCounts = (sw: KavachSwitch): { up: number; down: number } => {
  const ports = Object.values(sw.ports);
  const up = ports.filter(p => p.status === 'UP').length;
  const down = ports.filter(p => p.status === 'DOWN').length;
  return { up, down };
};

/**
 * Format port status for display
 * @param status - Port status 'UP' or 'DOWN'
 * @returns Formatted status with color class
 */
export const formatPortStatus = (status: 'UP' | 'DOWN'): { text: string; colorClass: string } => {
  return {
    text: status,
    colorClass: status === 'UP' ? 'status-up' : 'status-down',
  };
};

/**
 * Get severity color class for alerts
 * @param alert - KavachAlert object
 * @returns CSS class for the alert type
 */
export const getAlertColorClass = (alert: KavachAlert): string => {
  return alert.type === 'critical' ? 'alert-critical' : 'alert-warning';
};

/**
 * Sort switches by health status (critical first, then partial, then healthy)
 * @param switches - Array of KavachSwitch objects
 * @returns Sorted array
 */
export const sortSwitchesByHealth = (switches: KavachSwitch[]): KavachSwitch[] => {
  const healthOrder = { critical: 0, partial: 1, healthy: 2 };
  return [...switches].sort((a, b) => {
    const healthA = getSwitchHealth(a);
    const healthB = getSwitchHealth(b);
    return healthOrder[healthA] - healthOrder[healthB];
  });
};

/**
 * Filter switches by station
 * @param switches - Array of KavachSwitch objects
 * @param station - Station name or 'ALL'
 * @returns Filtered array
 */
export const filterSwitchesByStation = (switches: KavachSwitch[], station: string): KavachSwitch[] => {
  if (station === 'ALL') return switches;
  return switches.filter(sw => sw.station === station);
};

/**
 * Search switches by name or IP
 * @param switches - Array of KavachSwitch objects
 * @param searchQuery - Search string
 * @returns Filtered array
 */
export const searchSwitches = (switches: KavachSwitch[], searchQuery: string): KavachSwitch[] => {
  if (!searchQuery.trim()) return switches;
  const query = searchQuery.toLowerCase().trim();
  return switches.filter(sw =>
    sw.name.toLowerCase().includes(query) ||
    sw.assetDetails.ipAddress.toLowerCase().includes(query) ||
    sw.station.toLowerCase().includes(query)
  );
};

/**
 * Calculate statistics from switches array
 * @param switches - Array of KavachSwitch objects
 * @returns Stats object with totalSwitches, portsUp, portsDown, etc.
 */
export const calculateStats = (switches: KavachSwitch[]) => {
  let portsUp = 0;
  let portsDown = 0;
  let criticalCount = 0;

  switches.forEach((sw) => {
    const portCounts = getPortCounts(sw);
    portsUp += portCounts.up;
    portsDown += portCounts.down;

    const health = getSwitchHealth(sw);
    if (health === 'critical') criticalCount++;
  });

  const totalPorts = portsUp + portsDown;
  const upPercent = totalPorts > 0 ? (portsUp / totalPorts) * 100 : 0;
  const downPercent = totalPorts > 0 ? (portsDown / totalPorts) * 100 : 0;

  return {
    totalSwitches: switches.length,
    portsUp,
    portsDown,
    activeAlerts: criticalCount,
    upTrend: parseFloat(upPercent.toFixed(1)),
    downTrend: parseFloat((-downPercent).toFixed(1)),
  };
};

// Chart default options
export const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
  },
  scales: {
    y: { beginAtZero: true },
  },
};

// Polling interval for auto-refresh (in milliseconds)
export const POLLING_INTERVAL = 30000; // 30 seconds

// Page size options
export const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
export const DEFAULT_PAGE_SIZE = 20;
